import { z } from 'zod';

export const CLOUD_INVENTORY_PROVIDERS = {
  AWS: 'aws',
  GCP: 'gcp',
  AZURE: 'azure',
  CLOUDFLARE: 'cloudflare',
} as const;

export const CloudInventoryProviderSchema = z.enum([
  CLOUD_INVENTORY_PROVIDERS.AWS,
  CLOUD_INVENTORY_PROVIDERS.GCP,
  CLOUD_INVENTORY_PROVIDERS.AZURE,
  CLOUD_INVENTORY_PROVIDERS.CLOUDFLARE,
]);

export type CloudInventoryProvider = z.infer<
  typeof CloudInventoryProviderSchema
>;

export const CLOUD_IP_RESOURCE_KINDS = {
  ELASTIC_IP: 'elastic_ip',
  ENI_PUBLIC: 'eni_public',
  PUBLIC_IP: 'public_ip',
  PREFIX: 'prefix',
  BYOIP: 'byoip',
  LB_FRONTEND: 'lb_frontend',
  NAT: 'nat',
  DNS_ORIGIN: 'dns_origin',
} as const;

export const CloudIpResourceKindSchema = z.enum([
  CLOUD_IP_RESOURCE_KINDS.ELASTIC_IP,
  CLOUD_IP_RESOURCE_KINDS.ENI_PUBLIC,
  CLOUD_IP_RESOURCE_KINDS.PUBLIC_IP,
  CLOUD_IP_RESOURCE_KINDS.PREFIX,
  CLOUD_IP_RESOURCE_KINDS.BYOIP,
  CLOUD_IP_RESOURCE_KINDS.LB_FRONTEND,
  CLOUD_IP_RESOURCE_KINDS.NAT,
  CLOUD_IP_RESOURCE_KINDS.DNS_ORIGIN,
]);

export type CloudIpResourceKind = z.infer<typeof CloudIpResourceKindSchema>;

export const CLOUD_IP_OWNERSHIP = {
  CUSTOMER_OWNED: 'customer_owned',
  SHARED_ANYCAST: 'shared_anycast',
  EPHEMERAL: 'ephemeral',
} as const;

export const CloudIpOwnershipSchema = z.enum([
  CLOUD_IP_OWNERSHIP.CUSTOMER_OWNED,
  CLOUD_IP_OWNERSHIP.SHARED_ANYCAST,
  CLOUD_IP_OWNERSHIP.EPHEMERAL,
]);

export type CloudIpOwnership = z.infer<typeof CloudIpOwnershipSchema>;

export const CloudAccountSnapshotSchema = z.object({
  provider: CloudInventoryProviderSchema,
  accountRef: z.string().min(1),
  displayName: z.string().min(1),
  parentAccountRef: z.string().nullable(),
});

export type CloudAccountSnapshot = z.infer<typeof CloudAccountSnapshotSchema>;

export const CloudAllocationSnapshotSchema = z.object({
  provider: CloudInventoryProviderSchema,
  accountRef: z.string().min(1),
  accountDisplayName: z.string().min(1),
  parentAccountRef: z.string().nullable(),
  ipOrCidr: z.string().min(1),
  version: z.union([z.literal(4), z.literal(6)]),
  resourceKind: CloudIpResourceKindSchema,
  resourceId: z.string().min(1),
  region: z.string().nullable(),
  associatedResourceId: z.string().nullable(),
  ownership: CloudIpOwnershipSchema,
  observedAt: z.coerce.date(),
});

export type CloudAllocationSnapshot = z.infer<
  typeof CloudAllocationSnapshotSchema
>;

export const CloudInventoryProviderSettingsSchema = z.object({
  inventorySurfaceIds: z.array(z.string()).catch([]).default([]),
});

export type CloudInventoryProviderSettings = z.infer<
  typeof CloudInventoryProviderSettingsSchema
>;

export const parseCloudInventoryProviderSettings = ({
  value,
}: {
  value: unknown;
}): CloudInventoryProviderSettings => {
  const parsed = CloudInventoryProviderSettingsSchema.safeParse(value ?? {});
  if (!parsed.success) {
    return { inventorySurfaceIds: [] };
  }
  return parsed.data;
};

export const formatCloudProviderLabel = ({
  provider,
}: {
  provider: string;
}): string => {
  switch (provider) {
    case CLOUD_INVENTORY_PROVIDERS.AWS:
      return 'AWS';
    case CLOUD_INVENTORY_PROVIDERS.GCP:
      return 'GCP';
    case CLOUD_INVENTORY_PROVIDERS.AZURE:
      return 'Azure';
    case CLOUD_INVENTORY_PROVIDERS.CLOUDFLARE:
      return 'Cloudflare';
    default:
      return provider;
  }
};

export const CLOUD_FRONTING_KINDS = {
  CDN: 'cdn',
  ACCELERATOR: 'accelerator',
  FRONT_DOOR: 'front_door',
  LOAD_BALANCER: 'load_balancer',
  PROXY: 'proxy',
} as const;

export const CloudFrontingKindSchema = z.enum([
  CLOUD_FRONTING_KINDS.CDN,
  CLOUD_FRONTING_KINDS.ACCELERATOR,
  CLOUD_FRONTING_KINDS.FRONT_DOOR,
  CLOUD_FRONTING_KINDS.LOAD_BALANCER,
  CLOUD_FRONTING_KINDS.PROXY,
]);

export type CloudFrontingKind = z.infer<typeof CloudFrontingKindSchema>;

export const CLOUD_FRONTING_KIND_TO_GATEWAY_CATEGORY = {
  [CLOUD_FRONTING_KINDS.CDN]: 'CDN',
  [CLOUD_FRONTING_KINDS.ACCELERATOR]: 'LOAD_BALANCER',
  [CLOUD_FRONTING_KINDS.FRONT_DOOR]: 'CDN',
  [CLOUD_FRONTING_KINDS.LOAD_BALANCER]: 'LOAD_BALANCER',
  [CLOUD_FRONTING_KINDS.PROXY]: 'PROXY',
} as const;

export const CloudFrontingServiceSnapshotSchema = z.object({
  provider: CloudInventoryProviderSchema,
  accountRef: z.string().min(1),
  hostname: z.string().min(1),
  frontingKind: CloudFrontingKindSchema,
  providerLabel: z.string().min(1),
  aliases: z.array(z.string()),
  originHosts: z.array(z.string()),
  ips: z.array(z.string()),
  resourceId: z.string().min(1),
  region: z.string().nullable(),
  enabled: z.boolean(),
  observedAt: z.coerce.date(),
});

export type CloudFrontingServiceSnapshot = z.infer<
  typeof CloudFrontingServiceSnapshotSchema
>;

export const CLOUD_STORAGE_PUBLIC_ACCESS = {
  PUBLIC: 'PUBLIC',
  RESTRICTED: 'RESTRICTED',
  UNKNOWN: 'UNKNOWN',
} as const;

export const CloudStoragePublicAccessSchema = z.enum([
  CLOUD_STORAGE_PUBLIC_ACCESS.PUBLIC,
  CLOUD_STORAGE_PUBLIC_ACCESS.RESTRICTED,
  CLOUD_STORAGE_PUBLIC_ACCESS.UNKNOWN,
]);

export type CloudStoragePublicAccess = z.infer<
  typeof CloudStoragePublicAccessSchema
>;

export const CloudStorageServiceSnapshotSchema = z.object({
  provider: CloudInventoryProviderSchema,
  accountRef: z.string().min(1),
  name: z.string().min(1),
  endpointHost: z.string().min(1),
  resourceId: z.string().min(1),
  region: z.string().nullable(),
  publicAccess: CloudStoragePublicAccessSchema,
  websiteEnabled: z.boolean(),
  observedAt: z.coerce.date(),
});

export type CloudStorageServiceSnapshot = z.infer<
  typeof CloudStorageServiceSnapshotSchema
>;
